import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import Button from '../components/ui/Button';
import LocalizedNumberInput from '../components/ui/LocalizedNumberInput';
import { formatCurrencyAr, parseLocalizedNumber } from '../utils/numberFormat';
import {
  formatPaymentConditionLabel,
  getClientPaymentConditionDays,
} from '../utils/salesReceivables';
import {
  ArrowLeft, Save, Briefcase, MapPinned, Landmark, Mail, Smartphone, RefreshCcw, User, FileText, CreditCard, Power, ShieldAlert
} from 'lucide-react';

const ADMIN_ROLES = new Set(['admin', 'administrador']);

const SectionTitle = ({ icon, title }) => (
  <div className="bg-[#0B2A36] px-4 py-3 sm:px-5 border-b border-white/10 flex items-center gap-3">
    {icon}
    <h3 className="text-[11px] font-black uppercase tracking-widest text-white">{title}</h3>
  </div>
);

const Input = ({ label, className = '', ...props }) => (
  <div className={className}>
    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 block mb-1">{label}</label>
    <input
      {...props}
      className={`w-full bg-slate-50 border border-slate-100 px-4 py-3 rounded-xl text-[11px] font-black uppercase tracking-tight outline-none focus:border-[#51A7FD] ${props.className || ''}`}
    />
  </div>
);

function ClientesForm({ id, onNavigate }) {
  const params = useParams();
  const clientId = id || params?.id || null;
  const { profile, organization, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(false);
  const [uiMessage, setUiMessage] = useState(null);
  const [inlinePrompt, setInlinePrompt] = useState(null);
  const [priceLists, setPriceLists] = useState([]);

  const normalizeDocNumber = (docType, value) => {
    const raw = String(value || '').trim();
    if (!raw) return '';
    const digits = raw.replace(/\D/g, '');
    if (['CUIT', 'CUIL', 'DNI'].includes(String(docType || '').toUpperCase())) return digits;
    return raw;
  };

  const [form, setForm] = useState({
    full_name: '',
    doc_type: 'CUIT',
    doc_number: '',
    tax_condition: 'Responsable Inscripto',
    address: '',
    city: '',
    zip_code: '',
    state: '',
    email: '',
    phone: '',
    payment_condition_days: 0,
    credit_limit: 0,
    balance: 0,
    is_online_client: false,
    price_list_id: '',
    is_active: true,
    deactivated_at: null,
    deactivation_reason: '',
  });

  const [statusModal, setStatusModal] = useState(null); // { mode: 'deactivate' | 'activate', reason: string }
  const [savingStatus, setSavingStatus] = useState(false);
  const isAdmin = ADMIN_ROLES.has(String(profile?.role || '').toLowerCase());
  const allowClientDeactivation = organization?.allow_client_deactivation === true;

  useEffect(() => {
    if (clientId) loadCliente();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  useEffect(() => {
    if (!profile?.organization_id) return;
    loadPriceLists();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.organization_id]);

  async function loadPriceLists() {
    try {
      const { data, error } = await supabase
        .from('price_lists')
        .select('id, name, is_active')
        .eq('organization_id', profile.organization_id)
        .eq('is_active', true)
        .order('name', { ascending: true });

      if (error) throw error;
      setPriceLists(data || []);
    } catch (err) {
      console.error(err);
      setPriceLists([]);
    }
  }

  async function loadCliente() {
    setLoading(true);
    try {
      const { data, error } = await supabase.from('clients').select('*').eq('id', clientId).single();
      if (error) throw error;
      if (data) {
        setForm({
          ...data,
          doc_number: data.doc_number || '',
          tax_condition: data.tax_condition || 'Responsable Inscripto',
          payment_condition_days: getClientPaymentConditionDays(data),
          price_list_id: data.price_list_id || '',
          is_active: data.is_active !== false,
          deactivated_at: data.deactivated_at || null,
          deactivation_reason: data.deactivation_reason || '',
        });
      }
    } catch (err) {
      setUiMessage({ type: 'error', text: 'Error de sincronizacion. Verifica conexion y permisos.' });
    } finally {
      setLoading(false);
    }
  }

  const openStatusModal = (mode) => {
    if (!isAdmin) {
      setUiMessage({ type: 'error', text: 'Solo un administrador puede activar o desactivar clientes.' });
      return;
    }
    if (mode === 'deactivate' && !allowClientDeactivation) {
      setUiMessage({ type: 'error', text: 'La baja de clientes está deshabilitada en Mi Empresa.' });
      return;
    }
    setStatusModal({ mode, reason: mode === 'deactivate' ? '' : (form.deactivation_reason || '') });
  };

  const handleConfirmStatusChange = async () => {
    if (!statusModal || !clientId || !profile?.organization_id) return;
    const isDeactivating = statusModal.mode === 'deactivate';
    const trimmedReason = String(statusModal.reason || '').trim();

    setSavingStatus(true);
    try {
      const payload = isDeactivating
        ? {
            is_active: false,
            deactivated_at: new Date().toISOString(),
            deactivation_reason: trimmedReason || null,
          }
        : {
            is_active: true,
            deactivated_at: null,
            deactivation_reason: null,
          };

      const { error } = await supabase
        .from('clients')
        .update(payload)
        .eq('organization_id', profile.organization_id)
        .eq('id', clientId);

      if (error) throw error;

      setForm((current) => ({ ...current, ...payload }));
      setStatusModal(null);
      setUiMessage({
        type: 'success',
        text: isDeactivating
          ? 'Cliente desactivado. Se mantiene su historial pero ya no aparecerá en selectores ni en la tienda.'
          : 'Cliente reactivado.',
      });
    } catch (err) {
      console.error(err);
      setUiMessage({ type: 'error', text: 'No se pudo cambiar el estado del cliente.' });
    } finally {
      setSavingStatus(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!profile?.organization_id) {
      setUiMessage({ type: 'error', text: 'Sesion invalida. Reingresa al sistema.' });
      return;
    }

    setLoading(true);
    try {
      const docTypeNorm = String(form.doc_type || '').trim();
      const docNumberNorm = normalizeDocNumber(docTypeNorm, form.doc_number);
      const docNumberDb = docNumberNorm ? docNumberNorm : null;
      const paymentConditionDays = Math.max(0, Math.floor(Number(form.payment_condition_days) || 0));

      if (!clientId && docTypeNorm && docNumberDb) {
        const { data: existing, error: exErr } = await supabase
          .from('clients')
          .select('id, full_name')
          .eq('organization_id', profile.organization_id)
          .eq('doc_type', docTypeNorm)
          .eq('doc_number', docNumberDb)
          .limit(1);

        if (!exErr && existing && existing.length > 0) {
          const ex = existing[0];
          setInlinePrompt({ id: ex.id, full_name: ex.full_name, doc_type: docTypeNorm, doc_number: docNumberNorm });
          setUiMessage({ type: 'info', text: `Ya existe un cliente con ese documento (${docTypeNorm} ${docNumberNorm}).` });
          setLoading(false);
          return;
        }
      }

      const dbPayload = {
        full_name: form.full_name,
        doc_type: docTypeNorm || null,
        doc_number: docNumberDb,
        tax_condition: form.tax_condition,
        address: form.address,
        city: form.city,
        zip_code: form.zip_code,
        state: form.state,
        email: form.email,
        phone: form.phone,
        default_payment_condition: formatPaymentConditionLabel(paymentConditionDays),
        payment_condition_days: paymentConditionDays,
        credit_limit: parseLocalizedNumber(form.credit_limit) ?? 0,
        balance: parseLocalizedNumber(form.balance) ?? 0,
        organization_id: profile.organization_id,
        is_online_client: form.is_online_client,
        price_list_id: form.price_list_id || null
      };

      const { error } = clientId
        ? await supabase.from('clients').update(dbPayload).eq('id', clientId)
        : await supabase.from('clients').insert([dbPayload]);

      if (error) throw error;

      setUiMessage({ type: 'success', text: clientId ? 'Cliente actualizado.' : 'Cliente registrado.' });
      onNavigate('clientes');
    } catch (err) {
      const code = String(err?.code || '');
      const msg = String(err?.message || '');
      const details = String(err?.details || '');
      const isDupe = code === '23505' || /duplicate key value/i.test(msg) || /uq_clients_org_doc_activos/i.test(details);

      if (isDupe) {
        setUiMessage({ type: 'error', text: 'Ya existe un cliente activo con ese documento.' });
        return;
      }

      setUiMessage({ type: 'error', text: 'Error al guardar. Revisa los datos cargados.' });
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || (loading && !form.full_name && clientId)) {
    return (
      <div className="h-full flex items-center justify-center bg-[#F8FAFC]">
        <RefreshCcw className="animate-spin text-[#51A7FD]" size={32} />
      </div>
    );
  }

  return (
    <div className="w-full px-3 py-3 sm:px-4 sm:py-4 lg:px-5 bg-[#F8FAFC] min-h-screen font-sans text-slate-700 animate-in fade-in duration-500">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 mb-3 px-1">
        <div className="flex items-center gap-3">
          <button onClick={() => onNavigate('clientes')} className="p-2.5 bg-white border border-slate-200 rounded-xl text-slate-400 hover:text-[#51A7FD] shadow-sm transition-all">
            <ArrowLeft size={20} />
          </button>
          <div className="w-9 h-9 sm:w-10 sm:h-10 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-md shrink-0">
            <User size={18} className="text-[#3FF2AA]" />
          </div>
          <div>
            <h1 className="text-lg sm:text-xl lg:text-2xl font-black text-slate-800 tracking-tight uppercase">
              {clientId ? 'Editar Cliente' : 'Nuevo Cliente'}
            </h1>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
              {clientId ? 'Actualizacion de legajo comercial y fiscal' : 'Alta de cliente para ventas y cuenta corriente'}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2.5 mb-3">
        <div className="bg-white p-3.5 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md hover:border-blue-300/30 transition-all duration-300 group">
            <div className="flex items-center justify-between">
              <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Modo</p>
              <div className="p-1.5 rounded-lg bg-blue-50 text-blue-600 group-hover:scale-110 transition-transform">
                <FileText size={16} />
              </div>
            </div>
            <p className="mt-1 text-lg font-black uppercase text-blue-600 tracking-tight">{clientId ? 'Edicion' : 'Alta'}</p>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
              {clientId ? 'Actualizando ficha existente' : 'Creacion de nuevo legajo'}
            </p>
        </div>
        <div className="bg-white p-3.5 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md hover:border-slate-300 transition-all duration-300 group">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Documento</p>
            <div className="p-1.5 rounded-lg bg-slate-50 text-slate-600 group-hover:scale-110 transition-transform">
              <Briefcase size={16} />
            </div>
          </div>
          <p className="mt-1 text-lg font-black uppercase text-slate-800 tracking-tight">{form.doc_type || 'CUIT'}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 truncate">{form.doc_number || 'Sin numero cargado'}</p>
        </div>
        <div className="bg-white p-3.5 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md hover:border-emerald-300/30 transition-all duration-300 group sm:col-span-2 xl:col-span-1">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Credito inicial</p>
            <div className="p-1.5 rounded-lg bg-emerald-50 text-emerald-600 group-hover:scale-110 transition-transform">
              <CreditCard size={16} />
            </div>
          </div>
          <p className="mt-1 text-lg font-black text-emerald-600 tracking-tight">{formatCurrencyAr(parseLocalizedNumber(form.credit_limit) ?? 0)}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Limite definido en la ficha</p>
        </div>
      </div>

      {uiMessage?.text && (
        <div className={`mb-4 rounded-2xl border px-4 py-3 text-[11px] font-bold ${uiMessage.type === 'error' ? 'bg-rose-50 border-rose-200 text-rose-700' : uiMessage.type === 'success' ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-blue-50 border-blue-200 text-blue-700'}`}>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
            <div>{uiMessage.text}</div>
            {inlinePrompt?.id && (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => onNavigate?.('clientes-detalle', inlinePrompt.id)}
                  className="px-3 py-2 rounded-xl bg-[#51A7FD] text-white text-[10px] font-black uppercase tracking-widest hover:opacity-90"
                >
                  Abrir legajo
                </button>
                <button
                  type="button"
                  onClick={() => { setInlinePrompt(null); setUiMessage(null); }}
                  className="px-3 py-2 rounded-xl bg-white border border-slate-200 text-slate-600 text-[10px] font-black uppercase tracking-widest hover:bg-slate-50"
                >
                  Cerrar
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-3 items-start">
      <form onSubmit={handleSave} className="space-y-3 xl:col-span-8">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <SectionTitle icon={<Briefcase size={16} className="text-[#51A7FD]" />} title="Identificacion y parametros fiscales" />
          <div className="p-4 sm:p-5 grid grid-cols-1 md:grid-cols-12 gap-x-4 gap-y-3">
            <Input
              label="Razon social / Nombre completo"
              className="md:col-span-12"
              type="text"
              data-onboarding-id="client-name-input"
              value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value.toUpperCase() })}
              placeholder="EJ: EMPRESA S.A. O JUAN PEREZ"
              required
            />

            <div className="md:col-span-4">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 block mb-1">Tipo de documento</label>
              <select className="w-full bg-slate-50 border border-slate-100 px-4 py-3 rounded-xl text-[11px] font-black uppercase tracking-tight outline-none focus:border-[#51A7FD]" value={form.doc_type} onChange={(e) => { setForm({ ...form, doc_type: e.target.value }); setInlinePrompt(null); }}>
                <option value="CUIT">CUIT</option>
                <option value="DNI">DNI</option>
                <option value="CUIL">CUIL</option>
              </select>
            </div>

            <Input
              label="Numero"
              className="md:col-span-4"
              type="text"
              value={form.doc_number}
              onChange={(e) => { setForm({ ...form, doc_number: e.target.value }); setInlinePrompt(null); }}
              placeholder="00-00000000-0"
            />

            <div className="md:col-span-4">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 block mb-1">Condicion ante IVA</label>
              <select className="w-full bg-slate-50 border border-slate-100 px-4 py-3 rounded-xl text-[11px] font-black uppercase tracking-tight outline-none focus:border-[#51A7FD]" value={form.tax_condition} onChange={(e) => setForm({ ...form, tax_condition: e.target.value })}>
                <option value="Responsable Inscripto">Responsable Inscripto</option>
                <option value="Monotributista">Monotributista</option>
                <option value="Consumidor Final">Consumidor Final</option>
              </select>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <SectionTitle icon={<MapPinned size={16} className="text-[#51A7FD]" />} title="Geolocalizacion y contacto" />
          <div className="p-4 sm:p-5 grid grid-cols-1 md:grid-cols-12 gap-x-4 gap-y-3">
            <Input label="Calle y altura" className="md:col-span-9" type="text" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            <Input label="CP" className="md:col-span-3" type="text" value={form.zip_code} onChange={(e) => setForm({ ...form, zip_code: e.target.value })} />
            <Input label="Ciudad" className="md:col-span-6" type="text" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
            <Input label="Provincia" className="md:col-span-6" type="text" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} />

            <div className="md:col-span-6">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 block mb-1">Email principal</label>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-300" size={14} />
                <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value.toLowerCase() })} className="w-full bg-slate-50 border border-slate-100 pl-10 pr-4 py-3 rounded-xl text-[11px] font-black uppercase tracking-tight outline-none focus:border-[#51A7FD]" />
              </div>
            </div>

            <div className="md:col-span-6">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 block mb-1">WhatsApp</label>
              <div className="relative">
                <Smartphone className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-300" size={14} />
                <input type="text" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full bg-slate-50 border border-slate-100 pl-10 pr-4 py-3 rounded-xl text-[11px] font-black uppercase tracking-tight outline-none focus:border-[#51A7FD]" />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <SectionTitle icon={<Landmark size={16} className="text-[#51A7FD]" />} title="Configuracion comercial" />
          <div className="p-4 sm:p-5 grid grid-cols-1 md:grid-cols-12 gap-x-4 gap-y-3">
            <div className="md:col-span-4">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 block mb-1">Limite de credito ($)</label>
              <LocalizedNumberInput
                decimals={2}
                selectOnFocus
                className="w-full bg-slate-50 border border-slate-100 px-4 py-3 rounded-xl text-[11px] font-black tracking-tight outline-none focus:border-[#51A7FD]"
                data-onboarding-id="client-credit-limit-input"
                value={form.credit_limit}
                onValueChange={(display) => setForm({ ...form, credit_limit: display })}
                placeholder="0,00"
              />
            </div>
            <div className="md:col-span-4">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 block mb-1">Saldo inicial</label>
              <LocalizedNumberInput
                decimals={2}
                selectOnFocus
                disabled={!!clientId}
                className="w-full bg-slate-50 border border-slate-100 px-4 py-3 rounded-xl text-[11px] font-black tracking-tight outline-none focus:border-[#51A7FD] disabled:opacity-60 disabled:bg-slate-100"
                value={form.balance}
                onValueChange={(display) => setForm({ ...form, balance: display })}
                placeholder="0,00"
              />
            </div>
            <div className="md:col-span-4">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 block mb-1">Condicion de pago (dias)</label>
              <input
                type="number"
                min="0"
                value={form.payment_condition_days}
                onChange={(e) => setForm({ ...form, payment_condition_days: e.target.value })}
                className="w-full bg-slate-50 border border-slate-100 px-4 py-3 rounded-xl text-[11px] font-black tracking-tight outline-none focus:border-[#51A7FD]"
                placeholder="0"
              />
              <p className="mt-1 ml-1 text-[9px] font-black uppercase tracking-widest text-slate-300">
                {formatPaymentConditionLabel(form.payment_condition_days)}
              </p>
            </div>
            <div className="md:col-span-12">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 block mb-1">Lista de precios asignada</label>
              <select
                value={form.price_list_id || ''}
                onChange={(e) => setForm({ ...form, price_list_id: e.target.value })}
                className="w-full bg-slate-50 border border-slate-100 px-4 py-3 rounded-xl text-[11px] font-black uppercase tracking-tight outline-none focus:border-[#51A7FD]"
              >
                <option value="">Sin lista especifica</option>
                {priceLists.map((priceList) => (
                  <option key={priceList.id} value={priceList.id}>
                    {priceList.name}
                  </option>
                ))}
              </select>
              <p className="mt-1 ml-1 text-[9px] font-black uppercase tracking-widest text-slate-300">
                Si el cliente tiene una lista asignada, POS y tienda virtual usaran esos precios.
              </p>
            </div>
          </div>
        </div>

        <div className="pt-2 pb-8">
          <Button
            data-onboarding-id="client-submit"
            type="submit"
            isLoading={loading}
            iconLeft={<Save size={18} />}
            className="w-full h-12 bg-[#3FF2AA] text-[#0B2A36] rounded-2xl shadow-lg shadow-emerald-100 hover:scale-[1.01] text-[10px] tracking-[0.3em]"
          >
            {clientId ? 'ACTUALIZAR CLIENTE' : 'GUARDAR NUEVO CLIENTE'}
          </Button>
        </div>
      </form>

      <aside className="xl:col-span-4 space-y-3">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="bg-[#0B2A36] px-4 py-3 sm:px-5 border-b border-white/10 flex items-center gap-3">
            <User size={16} className="text-[#3FF2AA]" />
            <h3 className="text-[11px] font-black uppercase tracking-widest text-white">Resumen rapido</h3>
          </div>
          <div className="p-4 sm:p-5 space-y-3">
            <div className="rounded-xl border border-slate-100 bg-slate-50 p-3">
              <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Nombre</p>
              <p className="mt-1 text-[13px] font-black text-slate-800 uppercase break-words">
                {form.full_name || 'Sin completar'}
              </p>
            </div>
            <div className="rounded-xl border border-slate-100 bg-slate-50 p-3">
              <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Identificacion</p>
              <p className="mt-1 text-[12px] font-black text-slate-800 uppercase">
                {(form.doc_type || 'CUIT')} {form.doc_number || 'Sin numero'}
              </p>
            </div>
            <div className="rounded-xl border border-slate-100 bg-slate-50 p-3">
              <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Condicion comercial</p>
              <p className="mt-1 text-[12px] font-black text-slate-800 uppercase">
                {formatPaymentConditionLabel(form.payment_condition_days)}
              </p>
              <p className="mt-1 text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                Credito: {formatCurrencyAr(parseLocalizedNumber(form.credit_limit) ?? 0)}
              </p>
              <p className="mt-1 text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                Lista: {priceLists.find((priceList) => priceList.id === form.price_list_id)?.name || 'Base'}
              </p>
            </div>
          </div>
        </div>

        {clientId && allowClientDeactivation && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="bg-[#0B2A36] px-4 py-3 sm:px-5 border-b border-white/10 flex items-center gap-3">
              <Power size={16} className="text-[#3FF2AA]" />
              <h3 className="text-[11px] font-black uppercase tracking-widest text-white">Estado del cliente</h3>
            </div>
            <div className="p-4 sm:p-5 space-y-3">
              <div className={`rounded-xl border p-3 ${form.is_active ? 'border-emerald-100 bg-emerald-50' : 'border-rose-100 bg-rose-50'}`}>
                <p className={`text-[9px] font-black uppercase tracking-widest ${form.is_active ? 'text-emerald-700' : 'text-rose-700'}`}>
                  {form.is_active ? 'Activo' : 'Inactivo'}
                </p>
                <p className="mt-1 text-[11px] font-bold leading-relaxed text-slate-700">
                  {form.is_active
                    ? 'Visible en selectores, reportes y tienda online.'
                    : 'Oculto de altas nuevas, reportes filtrados y tienda. El historial se conserva.'}
                </p>
                {!form.is_active && form.deactivated_at && (
                  <p className="mt-2 text-[10px] font-bold text-rose-700 uppercase tracking-widest">
                    Baja: {new Date(form.deactivated_at).toLocaleString('es-AR')}
                  </p>
                )}
                {!form.is_active && form.deactivation_reason && (
                  <p className="mt-1 text-[10px] font-bold text-slate-600 leading-snug">
                    Motivo: {form.deactivation_reason}
                  </p>
                )}
              </div>

              {isAdmin ? (
                <button
                  type="button"
                  onClick={() => openStatusModal(form.is_active ? 'deactivate' : 'activate')}
                  className={`w-full justify-center px-4 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center gap-2 transition-all shadow-md hover:scale-[1.01] ${form.is_active
                    ? 'bg-rose-600 text-white hover:bg-rose-700'
                    : 'bg-[#3FF2AA] text-[#0B2A36] hover:opacity-90'}`}
                >
                  <Power size={14} strokeWidth={3} />
                  {form.is_active ? 'Desactivar cliente' : 'Reactivar cliente'}
                </button>
              ) : (
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest text-center">
                  Solo administradores pueden cambiar el estado.
                </p>
              )}
            </div>
          </div>
        )}

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="bg-[#0B2A36] px-4 py-3 sm:px-5 border-b border-white/10 flex items-center gap-3">
            <CreditCard size={16} className="text-[#3FF2AA]" />
            <h3 className="text-[11px] font-black uppercase tracking-widest text-white">Guia de carga</h3>
          </div>
          <div className="p-4 sm:p-5 space-y-3">
            <p className="text-[11px] font-bold text-slate-600 leading-relaxed">
              Completa primero los datos fiscales y luego la configuracion comercial para dejar el cliente listo para POS, cuenta corriente y facturacion.
            </p>
            <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-3">
              <p className="text-[9px] font-black uppercase tracking-widest text-emerald-700">Tip</p>
              <p className="mt-1 text-[11px] font-bold text-emerald-800 leading-relaxed">
                Si el cliente ya existe con el mismo documento, usa el aviso superior para abrir el legajo y evitar duplicados.
              </p>
            </div>
          </div>
        </div>
      </aside>
      </div>

      {statusModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center px-4">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-2xl w-full max-w-md overflow-hidden">
            <div className="bg-[#0B2A36] px-5 py-4 flex items-center gap-3">
              <ShieldAlert size={18} className="text-[#3FF2AA]" />
              <h3 className="text-[11px] font-black uppercase tracking-widest text-white">
                {statusModal.mode === 'deactivate' ? 'Desactivar cliente' : 'Reactivar cliente'}
              </h3>
            </div>
            <div className="p-5 space-y-3">
              {statusModal.mode === 'deactivate' ? (
                <>
                  <p className="text-[12px] font-bold text-slate-700 leading-relaxed">
                    Al desactivar el cliente:
                  </p>
                  <ul className="text-[11px] font-bold text-slate-600 leading-relaxed list-disc pl-5 space-y-1">
                    <li>Se oculta de los selectores de proyectos, tickets, reuniones y acuerdos.</li>
                    <li>Se bloquea el acceso a la tienda online y el portal de cliente.</li>
                    <li>Se filtra de los reportes (modo "Activos") pero conserva su historial.</li>
                    <li>Si tiene saldo deudor, las cuentas corrientes siguen vigentes.</li>
                  </ul>
                  <div>
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 block mb-1">
                      Motivo de la baja (opcional)
                    </label>
                    <textarea
                      rows={3}
                      value={statusModal.reason}
                      onChange={(e) => setStatusModal((s) => ({ ...s, reason: e.target.value }))}
                      placeholder="Ej: cliente migrado a otro CRM, cuenta cerrada, etc."
                      className="w-full bg-slate-50 border border-slate-100 px-4 py-2.5 rounded-xl text-[11px] font-bold tracking-tight outline-none focus:border-[#51A7FD] resize-none"
                    />
                  </div>
                </>
              ) : (
                <p className="text-[12px] font-bold text-slate-700 leading-relaxed">
                  El cliente volverá a aparecer en selectores, reportes y tienda online. Se limpiará la fecha y motivo de baja anteriores.
                </p>
              )}

              <div className="flex items-center gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setStatusModal(null)}
                  disabled={savingStatus}
                  className="flex-1 px-4 py-2.5 rounded-xl bg-white border border-slate-200 text-slate-600 text-[10px] font-black uppercase tracking-widest hover:bg-slate-50 disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleConfirmStatusChange}
                  disabled={savingStatus}
                  className={`flex-1 px-4 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest shadow-md disabled:opacity-50 ${statusModal.mode === 'deactivate'
                    ? 'bg-rose-600 text-white hover:bg-rose-700'
                    : 'bg-[#3FF2AA] text-[#0B2A36] hover:opacity-90'}`}
                >
                  {savingStatus ? 'Guardando...' : (statusModal.mode === 'deactivate' ? 'Confirmar baja' : 'Reactivar')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ClientesForm;

